import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { sendArcReset, sendArcResetMany, sendThreshold, sendThresholdAll, clampThreshold } from "../utils/deviceCommands";
import { isMonitoringSensorId } from "../utils/installationInfo";
import { isDeviceOnline } from "../utils/deviceStatus";

/** Same rule as Dashboard / Monitoring: IR IDs 1–12 with COM connected. */
const isLiveIrSensor = (sensor) =>
  isMonitoringSensorId(sensor?.sensor_id) && Number(sensor?.com_state) === 1;

const liveIrSensors = (sensors = []) => (sensors || []).filter(isLiveIrSensor);

export const ResetModal = ({ isOpen, onClose, deviceList = [] }) => {
  const [localDevices, setLocalDevices] = useState([]);
  const [loadingSensorId, setLoadingSensorId] = useState(null);
  const [isResettingTest, setIsResettingTest] = useState(false);
  const [pendingReset, setPendingReset] = useState(false);
  const resetTriggerRef = useRef(false);
  const resettingTargetsRef = useRef([]);

  // Sync local state with props
  useEffect(() => {
    if (isOpen) {
      /** An offline H-LINK cannot receive a reset, and its last arc state is stale. */
      const onlineDevices = deviceList.filter(isDeviceOnline);
      setLocalDevices((prev) => onlineDevices.map((inc) => {
        const existing = prev.find(d => d.device_info.serial_number === inc.device_info.serial_number);
        const updatedSensors = liveIrSensors(inc.sensors).map((s) => {
          let isUsed = true;
          if (existing) {
            const oldS = existing.sensors.find(os => os.sensor_id === s.sensor_id);
            if (oldS) isUsed = oldS.isUsed;
          }
          return { ...s, isUsed };
        });
        return { ...inc, sensors: updatedSensors };
      }));

      // Logic to check if reset was successful (state changed to 0)
      if (resetTriggerRef.current && resettingTargetsRef.current.length > 0) {
        const allReset = resettingTargetsRef.current.every((t) => {
          const dev = deviceList.find(d => d.device_info.serial_number === t.serial);
          return dev && t.ids.every(id => {
            const s = liveIrSensors(dev.sensors).find(sensor => sensor.sensor_id === id);
            return s && s.arc_state === 0;
          });
        });
        if (allReset) {
          setIsResettingTest(false);
          setPendingReset(false);
          resetTriggerRef.current = false;
          resettingTargetsRef.current = [];
        }
      }
    }
  }, [isOpen, deviceList]);

  const handleToggleUse = (serial, id) => {
    setLocalDevices(prev => prev.map(d => d.device_info.serial_number !== serial ? d : {
      ...d, sensors: d.sensors.map(s => s.sensor_id === id ? { ...s, isUsed: !s.isUsed } : s)
    }));
  };

  const getStateInfo = (sensor) => {
    if (!sensor.isUsed) return { text: "DISABLED", color: "bg-gray-500 text-white" };
    if (sensor.arc_state > 0) return { text: "⚠ ARC D.", color: "bg-red-600 text-white" };
    return { text: "NORMAL", color: "bg-green-500 text-white" };
  };

  // --- 1. Reset Single Sensor (cloud: arc_reset) ---
  const handleResetSensor = async (serial, id) => {
    const device = localDevices.find(d => d.device_info.serial_number === serial);
    if (!device) return;

    resetTriggerRef.current = true;
    resettingTargetsRef.current = [{ serial, ids: [id] }];
    setIsResettingTest(true);
    setPendingReset(true);
    setLoadingSensorId(`${serial}-${id}`);

    try {
      await sendArcReset(serial, id);
    } catch (e) {
      console.error("Reset failed", e);
      setIsResettingTest(false);
      setPendingReset(false);
    } finally {
      setTimeout(() => {
        setIsResettingTest(false);
        setPendingReset(false);
        setLoadingSensorId(null);
      }, 2000);
    }
  };

  // --- 2. Reset All: one HLINK-{serial}/HAECHIE/All command (sensor_id 0) per H-LINK ---
  const handleResetAll = async () => {
    const targets = [];
    const requests = [];

    localDevices.forEach(d => {
        const serial = d.device_info.serial_number;
        const warned = d.sensors.filter(s => s.arc_state > 0);
        const ids = warned.filter(s => s.isUsed).map(s => s.sensor_id);
        if (ids.length === 0) return;

        targets.push({ serial, ids });
        // Sensors switched off in USE must not be reset, so only then fall back to per-sensor commands.
        const someDisabled = warned.some(s => !s.isUsed);
        requests.push(someDisabled ? sendArcResetMany(serial, ids) : sendArcReset(serial, 0));
    });

    if (requests.length === 0) return alert("No sensors eligible for reset.");
    
    resetTriggerRef.current = true;
    resettingTargetsRef.current = targets;
    setIsResettingTest(true);
    setPendingReset(true);
    setLoadingSensorId("ALL");

    try {
      await Promise.all(requests);
    } catch (e) {
      console.error("Reset All failed", e);
      setIsResettingTest(false);
      setPendingReset(false);
    } finally {
      setTimeout(() => {
        setIsResettingTest(false);
        setPendingReset(false);
        setLoadingSensorId(null);
      }, 2000);
    }
  };

  if (!isOpen) return null;
  const arcSensors = (d) => (d.sensors || []).filter((s) => s.arc_state > 0);
  const devicesWithSensors = localDevices.filter((d) => arcSensors(d).length > 0);
  const totalWarnings = devicesWithSensors.reduce((acc, d) => acc + arcSensors(d).filter(s => s.isUsed).length, 0);
  const isLoaderVisible = isResettingTest && pendingReset;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[3000] p-2 sm:p-4">
      <div className="bg-[#2d3344] text-white w-[95%] sm:w-[90%] lg:w-[80%] max-w-6xl p-4 rounded-2xl shadow-xl relative max-h-[90vh] overflow-y-auto text-xs sm:text-sm">
        
        {/* Loader Overlay */}
        {isLoaderVisible && (
          <div className="absolute inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center rounded-xl">
            <span className="text-white flex items-center gap-2 font-semibold text-lg">
               <span className="animate-spin h-6 w-6 border-2 border-white border-t-transparent rounded-full"></span>
               Processing Reset...
            </span>
          </div>
        )}

        <button onClick={onClose} className="absolute top-1 right-3 text-xl font-bold hover:text-red-400">&times;</button>
        <h2 className="text-lg font-bold mb-4 text-center border-b border-gray-600 pb-2">Reset Sensor Warnings</h2>
        
        <div className="space-y-4">
            {devicesWithSensors.map((device) => (
                <div key={device.device_info.serial_number} className="border border-gray-500 rounded-lg overflow-hidden">
                    
                    {/* Device Header */}
                    <div className="bg-gray-700 px-4 py-2 flex justify-between items-center">
                        <div className="font-bold text-blue-300 text-sm">SN: {device.device_info.serial_number}</div>
                        <div className="text-xs text-gray-400 font-mono">{device.device_info.hlink_ip}</div>
                    </div>

                    {/* Column Headers */}
                    <div className="grid grid-cols-6 gap-2 bg-gray-600 px-3 py-1 text-xs font-semibold text-gray-300">
                        <div>ID</div>
                        <div>USE</div>
                        <div>NAME</div>
                        <div>RESET</div>
                        <div>ARC MON</div>
                        <div>STATE</div>
                    </div>

                    {/* Sensor List — COM-connected IR sensors with an active arc only */}
                    <div className="bg-[#232836] p-2">
                        {arcSensors(device).map((s, i) => {
                            const uniqueKey = `${device.device_info.serial_number}-${s.sensor_id}`;
                            const state = !s.isUsed ? {t:"DISABLED",c:"bg-gray-500"} : (s.arc_state > 0 ? {t:"⚠ ARC D.",c:"bg-red-600"} : {t:"NORMAL",c:"bg-green-500"});
                            const arcColor = s.arc_threshold > 0 && s.ir_temperature > s.arc_threshold ? "bg-yellow-400 text-black font-bold" : "bg-gray-600 text-white";
                            
                            return (
                                <div key={uniqueKey} className={`grid grid-cols-6 gap-2 items-center p-2 rounded mb-1 text-xs ${i%2===0?"bg-gray-800":"bg-gray-700/50"}`}>
                                    <div className="pl-1 font-mono">{s.sensor_id}</div>
                                    
                                    {/* Toggle Switch */}
                                    <div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input 
                                                type="checkbox" 
                                                className="sr-only peer" 
                                                checked={s.isUsed} 
                                                onChange={() => handleToggleUse(device.device_info.serial_number, s.sensor_id)} 
                                                disabled={isResettingTest} 
                                            />
                                            <div className={`w-9 h-5 bg-gray-500 rounded-full peer-checked:bg-green-500 transition-colors duration-300 ${isResettingTest ? "opacity-50" : ""}`}></div>
                                            <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-300 peer-checked:translate-x-full"></div>
                                        </label>
                                    </div>

                                    <div className="truncate pr-2">{s.name || `Sensor-${s.sensor_id}`}</div>
                                    
                                    <div>
                                        <button 
                                            disabled={!s.isUsed || s.arc_state === 0 || loadingSensorId === uniqueKey || isResettingTest} 
                                            onClick={() => handleResetSensor(device.device_info.serial_number, s.sensor_id)} 
                                            className={`px-2 py-1 rounded font-bold transition w-full max-w-[60px] ${(!s.isUsed || s.arc_state === 0) ? "bg-gray-600 text-gray-400 cursor-not-allowed" : "bg-red-600 text-white hover:bg-red-700"}`}
                                        >
                                            RESET
                                        </button>
                                    </div>
                                    
                                    <div><span className={`px-2 py-0.5 rounded ${arcColor}`}>Monitoring</span></div>
                                    <div><span className={`px-2 py-0.5 rounded font-bold ${state.c} text-white`}>{state.t}</span></div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
            {devicesWithSensors.length === 0 && <div className="text-center text-gray-400 py-10">No sensors with an active arc</div>}
        </div>

        <div className="flex justify-center mt-6 border-t border-gray-600 pt-4">
          <button 
            disabled={isResettingTest || totalWarnings === 0} 
            onClick={handleResetAll} 
            className={`px-6 py-2 rounded-lg font-bold text-sm shadow-lg transition ${isResettingTest || totalWarnings === 0 ? "bg-gray-500 cursor-not-allowed" : "bg-red-600 hover:bg-red-700 text-white"}`}
          >
            RESET ALL WARNINGS
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};




export const ThresholdModal = ({ isOpen, onClose, deviceList = [] }) => {
  const [localDevices, setLocalDevices] = useState([]);
  const [globalThreshold, setGlobalThreshold] = useState(100);
  const [loading, setLoading] = useState(false);
  const [loaderText, setLoaderText] = useState("Updating Sensor Thresholds...");

  // --- Sync State ---
  useEffect(() => {
    if (isOpen && deviceList.length > 0) {
      setLocalDevices((prevLocal) => {
        return deviceList.map((incomingDevice) => {
          const existingDevice = prevLocal.find(d => d.device_info.serial_number === incomingDevice.device_info.serial_number);

          const mergedSensors = liveIrSensors(incomingDevice.sensors).map((incomingSensor) => {
            let existingSensor = null;
            if (existingDevice) {
              existingSensor = existingDevice.sensors.find(s => s.sensor_id === incomingSensor.sensor_id);
            }

            let isUsed = true;
            let editedThreshold = incomingSensor.arc_threshold || 100;
            let isModified = false;

            if (existingSensor) {
              isUsed = existingSensor.isUsed;
              if (existingSensor.isModified) editedThreshold = existingSensor.editedThreshold;
              isModified = editedThreshold !== incomingSensor.arc_threshold;
            }

            return {
              ...incomingSensor,
              isUsed,
              editedThreshold,
              isModified,
              defaultThreshold: 100
            };
          });

          return { ...incomingDevice, sensors: mergedSensors };
        });
      });
    }
  }, [isOpen, deviceList]);

  // --- Helper: Update Local State ---
  const updateSensorState = (serial, sensorId, updates) => {
    setLocalDevices(prev => prev.map(d => {
      if (d.device_info.serial_number !== serial) return d;
      return {
        ...d,
        sensors: d.sensors.map(s => {
          if (s.sensor_id !== sensorId) return s;
          const updatedSensor = { ...s, ...updates };
          if (updates.hasOwnProperty('editedThreshold')) {
             updatedSensor.isModified = updatedSensor.editedThreshold !== s.arc_threshold;
          }
          return updatedSensor;
        })
      };
    }));
  };

  const handleToggleUse = (serial, sensorId) => {
    const device = localDevices.find(d => d.device_info.serial_number === serial);
    const sensor = device?.sensors.find(s => s.sensor_id === sensorId);
    if (sensor) updateSensorState(serial, sensorId, { isUsed: !sensor.isUsed });
  };

  const handleValueChange = (serial, sensorId, newValue) => {
    const val = clampThreshold(newValue);
    updateSensorState(serial, sensorId, { editedThreshold: val });
  };

  const handleIncrement = (serial, sensorId, amount) => {
    const device = localDevices.find(d => d.device_info.serial_number === serial);
    const sensor = device?.sensors.find(s => s.sensor_id === sensorId);
    if (sensor) handleValueChange(serial, sensorId, sensor.editedThreshold + amount);
  };

  // --- API: Write Single Sensor (cloud: threshold) ---
  const handleWriteSingle = async (serial, sensorId) => {
    const device = localDevices.find(d => d.device_info.serial_number === serial);
    if (!device) return;

    const sensor = device.sensors.find(s => s.sensor_id === sensorId);

    if (!sensor || !sensor.isUsed) {
      alert("Please enable the sensor before writing.");
      return;
    }

    setLoading(true);
    setLoaderText(`Updating Sensor ${sensorId}...`);

    try {
      await sendThreshold(serial, sensorId, sensor.editedThreshold);
      updateSensorState(serial, sensorId, { 
        arc_threshold: clampThreshold(sensor.editedThreshold), 
        isModified: false 
      });
      setLoaderText("Success!");
    } catch (error) {
      console.error(error);
      setLoaderText(error?.message || "Error updating threshold.");
    } finally {
      setTimeout(() => setLoading(false), 1000);
    }
  };

  // --- API: Reset Single (To 100) ---
  const handleResetSingle = async (serial, sensorId) => {
    const device = localDevices.find(d => d.device_info.serial_number === serial);
    if(!device) return;

    handleValueChange(serial, sensorId, 100); 

    setLoading(true);
    setLoaderText("Resetting to Default (100)...");

    try {
      await sendThreshold(serial, sensorId, 100);
      updateSensorState(serial, sensorId, { 
        arc_threshold: 100, 
        editedThreshold: 100,
        isModified: false 
      });
      setLoaderText("Success!");
    } catch (e) { 
      console.error(e); 
      setLoaderText(e?.message || "Error resetting.");
    } finally {
      setTimeout(() => setLoading(false), 1000);
    }
  };

  // --- API: Apply Global Threshold ---
  const handleApplyAll = async () => {
    setLoading(true);
    setLoaderText(`Applying ${globalThreshold} to all enabled sensors...`);

    const requests = [];

    localDevices.forEach(device => {
      const serial = device.device_info.serial_number;
      
      const enabledSensors = device.sensors
        .filter(s => s.isUsed)
        .map(s => ({ 
            sensor_id: s.sensor_id, 
            threshold: globalThreshold,
        }));

      if (enabledSensors.length > 0) {
        const req = sendThresholdAll(serial, globalThreshold)
          .then(() => ({ serial, success: true }))
          .catch(() => ({ serial, success: false }));
        requests.push(req);
      }
    });

    if (requests.length === 0) {
      setLoading(false);
      alert("No enabled sensors found.");
      return;
    }

    try {
      const results = await Promise.all(requests);
      
      setLocalDevices(prev => prev.map(d => {
        const result = results.find(r => r.serial === d.device_info.serial_number);
        if (result && result.success) {
          return {
            ...d,
            sensors: d.sensors.map(s => {
              if (!s.isUsed || s.sensor_id > 12) return s;
              return { 
                ...s, 
                arc_threshold: globalThreshold, 
                editedThreshold: globalThreshold, 
                isModified: false 
              };
            })
          };
        }
        return d;
      }));
      setLoaderText("Global Update Complete!");
    } catch (e) {
      console.error(e);
      setLoaderText("Error in global update.");
    } finally {
      setTimeout(() => setLoading(false), 1500);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[3000] bg-black bg-opacity-50 flex justify-center items-center p-2 sm:p-4">
      {/* LAYOUT FIX: 
         - Changed 'block' to 'flex flex-col' so children stack vertically.
         - 'max-h-[90vh]' ensures the modal fits on screen.
      */}
      <div className="bg-[#1e1e2f] text-white rounded-xl shadow-lg w-[95%] sm:w-[90%] lg:w-[80%] max-w-6xl relative flex flex-col max-h-[90vh]">
        
        {loading && (
          <div className="absolute inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center rounded-xl">
             <div className="text-white text-lg font-semibold flex items-center gap-3">
               <span className="animate-spin h-6 w-6 border-4 border-white border-t-transparent rounded-full"></span>
               {loaderText}
             </div>
          </div>
        )}

        {/* --- HEADER --- */}
        {/* 'shrink-0' ensures the header doesn't squash when the list gets long */}
        <div className="p-4 border-b border-gray-600 relative shrink-0">
            <button onClick={onClose} className="absolute top-2 right-4 text-2xl hover:text-red-400">&times;</button>
            <h2 className="text-xl font-bold text-center">ARC Threshold Configuration</h2>
        </div>

        {/* --- SCROLLABLE CONTENT --- */}
        {/* - 'flex-1': Takes up all remaining space between Header and Footer.
           - 'overflow-y-auto': Only this section scrolls.
        */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {localDevices.filter((d) => (d.sensors || []).length > 0).map((device) => (
            <div key={device.device_info.serial_number} className="border border-gray-600 rounded-lg overflow-hidden">
                
                <div className="bg-gray-700 px-4 py-2 flex justify-between items-center">
                    <div className="flex flex-col">
                        <span className="font-bold text-blue-300">SN: {device.device_info.serial_number}</span>
                        <span className="text-xs text-gray-400">IP: {device.device_info.hlink_ip}</span>
                    </div>
                </div>

                <div className="hidden sm:grid grid-cols-7 gap-2 bg-gray-800 px-3 py-1 text-xs font-semibold text-gray-300 text-center items-center">
                    <div>ID</div>
                    <div>USE</div>
                    <div>LIVE</div>
                    <div>RESET</div>
                    <div className="col-span-2">ADJUST</div>
                    <div>WRITE</div>
                </div>

                <div className="bg-[#2c2c3c] p-2 space-y-1">
                    {device.sensors.map((s, idx) => (
                        <div key={`${device.device_info.serial_number}-${s.sensor_id}`} 
                             className={`flex flex-col sm:grid sm:grid-cols-7 gap-2 items-center p-2 rounded text-sm ${idx%2===0 ? "bg-gray-800/50" : "bg-transparent"}`}>
                            
                            {/* ID */}
                            <div className="flex justify-between w-full sm:w-auto sm:justify-center font-mono font-bold text-gray-400">
                                <span className="sm:hidden">ID:</span>
                                {s.sensor_id}
                            </div>

                            {/* Enable */}
                            <div className="flex justify-between w-full sm:w-auto sm:justify-center">
                                <span className="sm:hidden">Enable:</span>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input type="checkbox" className="sr-only peer" checked={s.isUsed} onChange={() => handleToggleUse(device.device_info.serial_number, s.sensor_id)} />
                                    <div className="w-9 h-5 bg-gray-600 rounded-full peer-checked:bg-green-500 transition-colors"></div>
                                    <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform peer-checked:translate-x-full"></div>
                                </label>
                            </div>

                            {/* Live Value */}
                            <div className="flex justify-between w-full sm:w-auto sm:justify-center">
                                <span className="sm:hidden">Live:</span>
                                <span className="font-mono text-yellow-500 font-bold">{s.arc_threshold}</span>
                            </div>

                            {/* Reset Button */}
                            <div className="flex justify-between w-full sm:w-auto sm:justify-center">
                                <span className="sm:hidden">Reset:</span>
                                <button 
                                    onClick={() => handleResetSingle(device.device_info.serial_number, s.sensor_id)}
                                    className="bg-red-700 hover:bg-red-700 text-white text-xs px-3 py-1.5 rounded w-full sm:w-auto"
                                >
                                    RST (100)
                                </button>
                            </div>

                            {/* Adjust Controls */}
                            <div className="col-span-2 flex items-center justify-center gap-2 w-full mt-2 sm:mt-0">
                                <button onClick={() => handleIncrement(device.device_info.serial_number, s.sensor_id, -10)} className="px-2 py-1 bg-gray-600 rounded hover:bg-gray-500 min-w-[30px]">-</button>
                                
                                <input 
                                    type="range" min="50" max="1500" step="10" 
                                    value={s.editedThreshold} 
                                    onChange={(e) => handleValueChange(device.device_info.serial_number, s.sensor_id, e.target.value)}
                                    className="accent-blue-500 w-full"
                                />
                                
                                <button onClick={() => handleIncrement(device.device_info.serial_number, s.sensor_id, 10)} className="px-2 py-1 bg-gray-600 rounded hover:bg-gray-500 min-w-[30px]">+</button>
                                
                                <input 
                                    type="number" 
                                    value={s.editedThreshold} 
                                    onChange={(e) => handleValueChange(device.device_info.serial_number, s.sensor_id, e.target.value)}
                                    className={`w-16 bg-gray-900 border rounded px-1 text-center text-sm ${s.isModified ? "border-yellow-500 text-yellow-300" : "border-gray-600"}`}
                                />
                            </div>

                            {/* Write Action */}
                            <div className="flex w-full sm:w-auto justify-center mt-2 sm:mt-0">
                                <button 
                                    onClick={() => handleWriteSingle(device.device_info.serial_number, s.sensor_id)}
                                    disabled={!s.isUsed || !s.isModified}
                                    className={`w-full sm:w-auto text-xs px-4 py-1.5 rounded font-bold shadow transition ${(!s.isUsed || !s.isModified) ? "bg-gray-600 text-gray-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700 text-white"}`}
                                >
                                    WRITE
                                </button>
                            </div>
                        </div>
                    ))}
                    {device.sensors.length === 0 && <div className="text-center text-gray-500 py-4">No sensors found</div>}
                </div>
            </div>
          ))}
        </div>

        {/* --- FOOTER --- */}
        {/* - Removed 'absolute bottom-0'.
            - Added 'shrink-0' so it maintains its size.
            - It now sits naturally at the bottom of the column.
        */}
        <div className="bg-[#252535] border-t border-gray-600 p-4 rounded-b-xl shrink-0 flex flex-col sm:flex-row justify-between items-center gap-4 z-10">
            <div className="flex items-center gap-4">
                <span className="font-semibold text-sm">Global Value:</span>
                <div className="flex items-center gap-2">
                    <button onClick={() => setGlobalThreshold(p => Math.max(50, p - 10))} className="px-3 py-1 bg-gray-700 rounded hover:bg-gray-600">-</button>
                    <input 
                        type="range" min="50" max="1500" step="10" 
                        value={globalThreshold} 
                        onChange={(e) => setGlobalThreshold(Number(e.target.value))}
                        className="accent-blue-500 w-32" 
                    />
                    <button onClick={() => setGlobalThreshold(p => Math.min(1500, p + 10))} className="px-3 py-1 bg-gray-700 rounded hover:bg-gray-600">+</button>
                    <span className="font-mono bg-gray-900 px-2 py-1 rounded w-12 text-center">{globalThreshold}</span>
                </div>
            </div>
            
            <button 
                onClick={handleApplyAll}
                disabled={localDevices.length === 0}
                className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded shadow-lg transition disabled:bg-gray-600 disabled:cursor-not-allowed"
            >
                APPLY GLOBAL TO ALL
            </button>
        </div>
      </div>
    </div>,
    document.body
  );
};